import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from './components/TokenAuthenticator';
import callApi from './utils/callApi';
import { format } from 'date-fns'; // Make sure to install this package: npm install date-fns
import EditWhiteboardModal from './components/EditWhiteboardModal';
import Spinner from './components/Spinner';
import Header from './components/Header';

const Dashboard = () => {
  const { user } = useContext(UserContext);

  const [whiteboards, setWhiteboards] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newWhiteboardSlug, setNewWhiteboardSlug] = useState('');
  const [newWhiteboardPassword, setNewWhiteboardPassword] = useState('');
  const [editingWhiteboard, setEditingWhiteboard] = useState(null);
  const [hoveredWhiteboard, setHoveredWhiteboard] = useState(null);
  const [isMulti, setIsMulti] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredPassword, setHoveredPassword] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user]);

  useEffect(() => {
    // Fetch whiteboards from API
    fetchWhiteboards();
  }, []);

  const fetchWhiteboards = async () => {
    try {
      const data = await callApi('whiteboard', 'GET');

      setWhiteboards(data?.whiteboards || []);
    } catch (error) {
      console.error('Error fetching whiteboards:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateWhiteboard = async () => {
    try {
      await callApi('whiteboard', 'POST', {
        body: {
          slug: newWhiteboardSlug,
          password: newWhiteboardPassword,
          is_multi: isMulti,
          is_public: isPublic,
        },
      });

      setIsModalOpen(false);
      navigate(`/e/${newWhiteboardSlug}`);
    } catch (error) {
      console.error('Error creating whiteboard:', error);
    }
  };

  const getPasswordDisplay = (password) => {
    if (!password) return '';
    if (password.length <= 2) return password;
    return '*'.repeat(password.length - 2) + password.slice(-2);
  };

  const handleEditWhiteboard = (whiteboard) => {
    setEditingWhiteboard(whiteboard);
  };

  const handleWhiteboardUpdate = () => {
    fetchWhiteboards();
  };

  const ToggleSwitch = ({ label, isChecked, onToggle }) => (
    <div className="flex items-center justify-between mt-4">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <button
        type="button"
        className={`${
          isChecked ? 'bg-blue-600' : 'bg-gray-200'
        } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
        onClick={onToggle}
      >
        <span
          className={`${
            isChecked ? 'translate-x-5' : 'translate-x-0'
          } pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
        >
          <i className={`bi ${isChecked ? 'bi-check' : 'bi-x'} text-xs text-gray-400`}></i>
        </span>
      </button>
    </div>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return format(new Date(dateString), 'MMM d, yyyy HH:mm');
  };

  const handleCreateNewWhiteboard = () => {
    setIsModalOpen(true);
  };

  const handleRowClick = (slug) => {
    navigate(`/e/${slug}`);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0">Your Whiteboards</h2>
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Create New Whiteboard
            </button>
          </div>

          {isLoading ? (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6 text-center">
                <Spinner />
              </div>
            </div>
          ) : whiteboards.length === 0 ? (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6 text-center">
                <i className="bi bi-clipboard text-6xl text-gray-400 mb-4"></i>
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">No Whiteboards Yet</h3>
                <p className="text-sm text-gray-500 mb-4">Get started by creating your first whiteboard!</p>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <i className="bi bi-plus-circle mr-2"></i>
                  Create a Whiteboard
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {whiteboards.map((whiteboard) => (
                  <li key={whiteboard.id}>
                    <div
                      className="block hover:bg-gray-50 cursor-pointer"
                      onMouseEnter={() => setHoveredWhiteboard(whiteboard.id)}
                      onMouseLeave={() => setHoveredWhiteboard(null)}
                      onClick={() => handleRowClick(whiteboard.slug)}
                    >
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center min-h-[24px] relative">
                            <p className="text-lg font-bold text-gray-700 truncate">
                              {whiteboard.slug}
                            </p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditWhiteboard(whiteboard);
                              }}
                              className="ml-2 text-gray-400 hover:text-gray-500 sm:hidden edit-button"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            {hoveredWhiteboard === whiteboard.id && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditWhiteboard(whiteboard);
                                }}
                                className="hidden sm:block ml-2 text-gray-400 hover:text-gray-500"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {whiteboard.password && (
                              <div className="flex items-center">
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                                  onMouseEnter={() => setHoveredPassword(whiteboard.id)}
                                  onMouseLeave={() => setHoveredPassword(null)}
                                >
                                  <i className="bi bi-lock-fill text-gray-400 mr-1"></i>
                                  {hoveredPassword === whiteboard.id ? whiteboard.password : getPasswordDisplay(whiteboard.password)}
                                </span>
                              </div>
                            )}
                            {whiteboard.is_multi && (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                <i className="bi bi-people-fill mr-1"></i>
                                Multi
                              </span>
                            )}
                            {whiteboard.is_public && (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                <i className="bi bi-share-fill mr-1"></i>
                                Public
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 sm:flex sm:justify-between">
                          <div className="sm:flex">
                            <p className="flex items-center text-sm text-gray-500">
                              <i className="bi bi-clock-history flex-shrink-0 mr-1 h-5 w-5 text-gray-400"></i>
                              {whiteboard.snapshot_count} snapshots
                            </p>
                          </div>
                          <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                            <p>
                              {!whiteboard.last_snapshot_date ? <i>Never edited</i> :
                                <i>Last edited on <time dateTime={whiteboard.last_snapshot_date}>{formatDate(whiteboard.last_snapshot_date)}</time></i>
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {editingWhiteboard && (
        <EditWhiteboardModal
          isOpen={!!editingWhiteboard}
          onClose={() => setEditingWhiteboard(null)}
          whiteboard={editingWhiteboard}
          onWhiteboardUpdate={handleWhiteboardUpdate}
        />
      )}

      {isModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Create New Whiteboard
                    </h3>
                    <div className="mt-2">
                      <input
                        type="text"
                        placeholder="Whiteboard Slug"
                        value={newWhiteboardSlug}
                        onChange={(e) => setNewWhiteboardSlug(e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                      <input
                        type="password"
                        placeholder="Password (optional)"
                        value={newWhiteboardPassword}
                        onChange={(e) => setNewWhiteboardPassword(e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                      <ToggleSwitch
                        label="Multiplayer Edit"
                        isChecked={isMulti}
                        onToggle={() => setIsMulti(!isMulti)}
                      />
                      <ToggleSwitch
                        label="Public Shareable"
                        isChecked={isPublic}
                        onToggle={() => setIsPublic(!isPublic)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleCreateWhiteboard}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Create
                </button>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
